import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpRequest, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, throwError, NEVER } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { AuthService } from '@auth/auth.service';
import { Router } from '@angular/router';
import { TokenService } from '@common/services/utility/token.service';
import { ConfigService } from '@common/services/config/config.service';
import { SessionDataService } from '@common/services/session/session-data.service';

enum RequestType {
  config,
  auth,
  ccTokenRefresh,
  fi,
  anonymousFi,
  none,
}

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private authService: AuthService,
    private tokenService: TokenService,
    private configService: ConfigService,
    private readonly sessionDataService: SessionDataService,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return from(this.getRequestType(req.url)).pipe(
      mergeMap((requestType) => {
        const applicationName = 'FiAgent';
        switch (requestType) {
          case RequestType.config:
          case RequestType.anonymousFi: {
            req = req.clone({
              setHeaders: {
                'x-tenant-id': this.configService.tenantId,
                'X-AST-SessionId': this.sessionDataService.get(SessionDataService.SessionId, ''),
                'X-AST-ClientApplicationId': applicationName,
              },
            });
            return next.handle(req);
          }
          case RequestType.fi:
          case RequestType.ccTokenRefresh: {
            return from(this.authService.signedIn(requestType === RequestType.ccTokenRefresh)).pipe(
              mergeMap((signedIn) => {
                if (!signedIn) {
                  return NEVER;
                }
                if (typeof req === 'object' && req !== null) {
                  const maybeRequest = req as { body: { AccessToken?: string } };
                  const body = {
                    ...maybeRequest.body,
                  };
                  req = req.clone({
                    body,
                    setHeaders: {
                      'X-AST-ContactCenter-Auth-Token': signedIn ? this.tokenService.contactCenterToken : '',
                      'x-tenant-id': this.configService.tenantId,
                      'X-AST-SessionId': this.sessionDataService.get(SessionDataService.SessionId, ''),
                      'X-AST-ClientApplicationId': applicationName,
                    },
                  });
                }

                return next.handle(req);
              }),
              catchError((error: HttpErrorResponse) => {
                if (error.status === 401) {
                  this.router.navigate(['/error-page'], {
                    queryParams: {
                      title: 'Authorization Error',
                      description:
                        'You are not authorized to enter Video Service application. Please request access from your manager.',
                    },
                  });
                }

                return throwError(error);
              }),
            );
          }
          case RequestType.auth:
          case RequestType.none: {
            return next.handle(req);
          }
        }
      }),
    );
  }

  async getRequestType(url: string): Promise<RequestType> {
    // order of conditions is important
    if (this.authService.isConfigRequest(url)) {
      return RequestType.config;
    }

    if (this.authService.isCCTokenRefreshRequest(url)) {
      return RequestType.ccTokenRefresh;
    }

    if (this.authService.isCognitoRequest(url)) {
      return RequestType.auth;
    }

    if (this.authService.isFiRequest(url)) {
      return RequestType.fi;
    }

    if (this.authService.isntNativeMfmFiRequest(url)) {
      return RequestType.anonymousFi;
    }

    return RequestType.none;
  }
}
